import { useEffect, useRef, useState } from "react";
import { X, Eye, User, Calculator } from "@phosphor-icons/react";
import type { LoteSVG } from "../../types/sitePlan";

interface Props {
  lote: LoteSVG;
  onClose: () => void;
  onCalculo?: (lote: LoteSVG) => void;
}

const fmtCRC = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-CR", {
        style: "currency",
        currency: "CRC",
        maximumFractionDigits: 2,
      }).format(n);

const fmtDate = (s: string | null | undefined) => {
  if (!s) return "—";
  const datePart = s.split("T")[0];
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
};

const STORAGE_KEY = "siteplan-lote-card-pos";
const CARD_WIDTH = 420;
const PADDING = 16;

interface Position {
  x: number;
  y: number;
}

function getDefaultPosition(): Position {
  // top-6 right-6 (24px desde top, 24px desde right)
  return {
    x: window.innerWidth - CARD_WIDTH - 24,
    y: 24,
  };
}

function clampToViewport(pos: Position, cardHeight: number): Position {
  const maxX = window.innerWidth - CARD_WIDTH - PADDING;
  const maxY = window.innerHeight - cardHeight - PADDING;
  return {
    x: Math.max(PADDING, Math.min(pos.x, maxX)),
    y: Math.max(PADDING, Math.min(pos.y, maxY)),
  };
}

export function LoteInfoPanel({ lote, onClose, onCalculo }: Props) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    dragging: boolean;
    offsetX: number;
    offsetY: number;
  }>({ dragging: false, offsetX: 0, offsetY: 0 });

  const [position, setPosition] = useState<Position>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return getDefaultPosition();
  });

  // Reclamp al cambiar tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      const h = cardRef.current?.offsetHeight ?? 600;
      setPosition((p) => clampToViewport(p, h));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reclamp inicial cuando se monta el card (por si las dudas)
  useEffect(() => {
    const h = cardRef.current?.offsetHeight ?? 600;
    setPosition((p) => clampToViewport(p, h));
  }, []);

  // Listeners globales para mousemove/mouseup mientras se arrastra
  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragStateRef.current.dragging) return;
      const next = {
        x: e.clientX - dragStateRef.current.offsetX,
        y: e.clientY - dragStateRef.current.offsetY,
      };
      const h = cardRef.current?.offsetHeight ?? 600;
      setPosition(clampToViewport(next, h));
    };

    const handleUp = () => {
      if (!dragStateRef.current.dragging) return;
      dragStateRef.current.dragging = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        // Persistir la posición final
        setPosition((p) => {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
          return p;
        });
      } catch {
        /* ignore */
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  const handleDragStart = (e: React.MouseEvent) => {
    // No iniciar drag si el click fue sobre el botón de cerrar
    if ((e.target as HTMLElement).closest("button")) return;
    dragStateRef.current = {
      dragging: true,
      offsetX: e.clientX - position.x,
      offsetY: e.clientY - position.y,
    };
    document.body.style.cursor = "grabbing";
    document.body.style.userSelect = "none";
  };

  return (
    <div
      ref={cardRef}
      className="fixed z-[60] w-[420px] max-h-[calc(100vh-2rem)] overflow-y-auto bg-white"
      style={{
        left: position.x,
        top: position.y,
        borderRadius: 16,
        boxShadow:
          "0 6px 0 rgba(0,0,0,0.16), 0 2px 0 rgba(0,0,0,0.16)",
        fontFamily: "Roboto, sans-serif",
      }}
    >
      {/* Header — arrastrable */}
      <div
        className="flex items-start justify-between px-6 pt-5 pb-3"
        style={{
          cursor: dragStateRef.current.dragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
        onMouseDown={handleDragStart}
      >
        <div>
          <div
            className="text-xs uppercase tracking-wider"
            style={{ color: "#747B86", letterSpacing: "0.4px" }}
          >
            Info Lote
          </div>
          <h2
            className="text-2xl font-semibold"
            style={{ color: "#5D636C", lineHeight: "32px" }}
          >
            {lote.lote}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:opacity-80"
          style={{ backgroundColor: "#BB4A4A" }}
          aria-label="Cerrar"
        >
          <X size={20} weight="bold" color="white" />
        </button>
      </div>

      {/* Dimensiones */}
      <div
        className="mx-6 mb-4 grid grid-cols-3 gap-3 px-4 py-3"
        style={{
          borderRadius: 8,
          backgroundColor: "#F3F3F3",
          border: "1px solid #EBEBEB",
        }}
      >
        <Stat label="Área" value={lote.area != null ? `${lote.area.toFixed(1)} m²` : "—"} />
        <Stat label="Frente" value={lote.frente != null ? `${lote.frente.toFixed(1)} m` : "—"} />
        <Stat label="Fondo" value={lote.fondo != null ? `${lote.fondo.toFixed(1)} m` : "—"} />
      </div>

      {/* Modelos */}
      <Section>
        <Field label="Modelo Tramitado" value={lote.modeloTramitado} />
        <button
          onClick={() => onCalculo?.(lote)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: "#2B7BD9", borderRadius: 8 }}
        >
          <Calculator size={16} weight="bold" />
          Cálculo
        </button>
      </Section>

      <Section>
        <Field label="Modelo Construido" value={lote.modeloConstruido} />
        <Field
          label="Actividad Constructiva"
          value={lote.actividadC}
          align="right"
        />
      </Section>

      {/* Estado pill */}
      <div className="mx-6 mb-4 flex overflow-hidden" style={{ borderRadius: 8 }}>
        <div
          className="flex-1 py-2 text-center text-sm font-semibold text-white"
          style={{ backgroundColor: lote.colorHex }}
        >
          {lote.estado}
        </div>
        {lote.banco && (
          <div
            className="px-6 py-2 text-center text-sm font-semibold text-white"
            style={{ backgroundColor: lote.colorHEXBan || "#1B3A6B" }}
          >
            {lote.banco}
          </div>
        )}
      </div>

      {/* Cliente */}
      {lote.cliente && (
        <div className="mx-6 mb-3 flex items-center gap-2">
          <User size={18} weight="bold" color="#5D636C" />
          <span className="text-sm font-semibold" style={{ color: "#5D636C" }}>
            {lote.cliente}
          </span>
        </div>
      )}

      <Section>
        <Field label="Modelo Caso" value={lote.modeloCaso} />
        <Field label="Vendedor" value={lote.vendedor} align="right" />
      </Section>

      {/* Precios + fechas */}
      <div className="mx-6 mb-5 grid grid-cols-[1fr_auto] gap-x-6 gap-y-3">
        <PriceRow label="Precio Venta" value={fmtCRC(lote.precioVenta)} bold />
        <DateRow letter="R" date={fmtDate(lote.fechaReserva)} />
        <PriceRow label="Precio Casa" value={fmtCRC(lote.precioCasa)} />
        <DateRow letter="PF" date={fmtDate(lote.fechaPF)} />
        <PriceRow label="Precio Lote" value={fmtCRC(lote.precioLote)} />
        <DateRow letter="E" date={fmtDate(lote.fechaEntrega)} />
      </div>

      {/* Acciones inferiores */}
      <div className="grid grid-cols-3 gap-2 px-6 pb-5">
        <ActionBtn label="Catastro" url={lote.linkCatastro} />
        <ActionBtn label="Construido" url={lote.azureCroquisConstruido} />
        <ActionBtn label="Tramitado" url={lote.azureCroquisTramitado} />
      </div>
    </div>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-6 mb-4 flex items-center justify-between gap-4">
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs" style={{ color: "#747B86" }}>
        {label}
      </div>
      <div
        className="text-base font-semibold"
        style={{ color: "#5D636C", fontFamily: "Roboto Mono, monospace" }}
      >
        {value}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  align = "left",
}: {
  label: string;
  value?: string | null;
  align?: "left" | "right";
}) {
  return (
    <div style={{ textAlign: align }}>
      <div className="text-xs" style={{ color: "#747B86" }}>
        {label}
      </div>
      <div className="text-sm font-semibold" style={{ color: "#5D636C" }}>
        {value || "—"}
      </div>
    </div>
  );
}

function PriceRow({
  label,
  value,
  bold = false,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div>
      <div className="text-xs" style={{ color: "#747B86" }}>
        {label}
      </div>
      <div
        className={bold ? "text-base font-semibold" : "text-sm"}
        style={{ color: "#5D636C", fontFamily: "Roboto Mono, monospace" }}
      >
        {value}
      </div>
    </div>
  );
}

function DateRow({ letter, date }: { letter: string; date: string }) {
  return (
    <div className="flex items-center gap-2 self-end">
      <div
        className="flex h-7 w-7 items-center justify-center text-xs font-semibold text-white"
        style={{ backgroundColor: "#5D636C", borderRadius: 4 }}
      >
        {letter}
      </div>
      <span className="text-sm" style={{ color: "#5D636C" }}>
        {date}
      </span>
    </div>
  );
}

function ActionBtn({ label, url }: { label: string; url: string | null }) {
  const enabled = !!url;
  return (
    <button
      onClick={() => enabled && window.open(url!, "_blank", "noopener,noreferrer")}
      disabled={!enabled}
      className="flex items-center justify-center gap-1 border py-2 text-xs font-semibold transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      style={{
        borderRadius: 8,
        borderColor: "#D9D9D9",
        color: "#5D636C",
      }}
      title={enabled ? `Abrir ${label}` : `${label} no disponible`}
    >
      <Eye size={14} weight="bold" />
      {label}
    </button>
  );
}